import { createSelector } from 'reselect'
import {
    ART_FILTER_FULL,
    DESIGN_SIDEPANEL_MODE_OPTIONS,
    DESIGN_SIDEPANEL_MODE_PRODUCT_BROWSE,
} from 'models/ui/constants'

import { getSelectedScene, getSelectedComposition } from 'models/compositions/selectors'

import { getUserId, getIsAdmin, getHasSeenMagicEraserTutorial } from 'models/user/selectors'
import { getPreviewSettings } from 'models/preview/selectors'
import { getDebugSettings } from 'models/admin/selectors'
import { getHasUserScenes } from 'models/scenes/selectors'
import optimizely from 'utils/optimizely'
import * as modes from 'models/ui/constants'

const getSceneSelectionTabShowStockRaw = (state) => state.ui.sceneSelectionTabShowStock
const getDesignSidepanelOpenRaw = (state) => state.ui.designSidepanelOpen

export const getCompositionsOpen = (state) => state.ui.compositionsOpen
export const getScenesOpen = (state) => state.ui.scenesOpen
export const getArtOpen = (state) => state.ui.artOpen
export const getDesignSidepanelModeRaw = (state) => state.ui.designSidepanelMode
export const getShouldPresentItemSelection = (state) => state.ui.shouldPresentItemSelection
export const getArtTrayFilter = (state) => state.ui.artTrayFilter || ART_FILTER_FULL
export const getShouldShowWelcomePopup = (state) => state.ui.shouldShowWelcomePopup
export const getSharePanelOpen = (state) => state.ui.sharePanelOpen
export const getFilterModalOpen = (state) => state.ui.filterModalOpen
export const getShowSwapMenu = (state) => state.ui.showSwapMenu
export const getShowMissingWallsModal = (state) => state.ui.showMissingWallsModal
export const getShowLoginPromptModal = (state) => state.ui.showLoginPromptModal
export const getShowDesignSummary = (state) => state.ui.showDesignSummary

export const getOrientation = (state) => state.ui.orientation

export const getSceneSelectionTabShowStock = createSelector(
    [getSceneSelectionTabShowStockRaw, getHasUserScenes],
    (showStock, hasUserScenes) => !hasUserScenes || showStock,
)

export const getCanPersonalize = createSelector(getSelectedComposition, (comp) => !!comp && !!comp.renderItems.length)

export const getAvailableItemSelections = createSelector(
    [getSelectedScene, getDebugSettings, getUserId, getIsAdmin],
    (selectedScene, debugSettings, userId, isAdmin) => {
        if (!selectedScene) {
            return { furniture: true, art: false }
        }
        const { sceneId } = selectedScene
        const userIdString = String(userId)
        const attributes = { userId: userIdString, isAdmin, sceneId: String(sceneId) }
        const wallsEnabledInOptimizely = optimizely.isFeatureEnabled('walls', userIdString, attributes)

        const artEnabled = selectedScene.hasWall && debugSettings.enableWalls && wallsEnabledInOptimizely
        const furnitureEnabled = !artEnabled || selectedScene.hasFloor // if no walls, allow furniture
        return {
            furniture: furnitureEnabled,
            art: artEnabled,
        }
    },
)

export const getDesigningEnabled = createSelector(
    [getPreviewSettings],
    (previewSettings) => !previewSettings.magicEraserMode,
)

export const getDesignSidepanelMode = createSelector(
    [getCanPersonalize, getDesignSidepanelModeRaw],
    (canPersonalize, mode) => {
        if (mode === DESIGN_SIDEPANEL_MODE_OPTIONS && !canPersonalize) {
            return DESIGN_SIDEPANEL_MODE_PRODUCT_BROWSE
        }
        return mode
    },
)

export const getDesignSidepanelOpen = createSelector(
    [getDesignSidepanelOpenRaw, getDesigningEnabled],
    (isOpen, canBeOpen) => canBeOpen && isOpen,
)

export const getAreOptionsVisible = createSelector(
    [getDesignSidepanelOpen, getDesignSidepanelMode],
    (isOpen, mode) => isOpen && mode === DESIGN_SIDEPANEL_MODE_OPTIONS,
)

export const getShowMagicEraserTutorial = createSelector(
    [getHasSeenMagicEraserTutorial, getPreviewSettings],
    (hasSeen, previewSettings) => !hasSeen && previewSettings.magicEraserMode,
)

export const getSelectedSidepanelTabs = createSelector([getDesignSidepanelMode], (mode) => ({
    isSwapSelected: [modes.DESIGN_SIDEPANEL_MODE_SWAP_BROWSE, modes.DESIGN_SIDEPANEL_MODE_SWAP_DETAILS].includes(mode),
    isBrowseSelected: [
        modes.DESIGN_SIDEPANEL_MODE_PRODUCT_BROWSE,
        modes.DESIGN_SIDEPANEL_MODE_PRODUCT_DETAILS,
    ].includes(mode),
    isMyListSelected: [
        modes.DESIGN_SIDEPANEL_MODE_MYITEMS_BROWSE,
        modes.DESIGN_SIDEPANEL_MODE_MYITEMS_DETAILS,
    ].includes(mode),
    isFavoritesSelected: [
        modes.DESIGN_SIDEPANEL_MODE_FAVORITES_BROWSE,
        modes.DESIGN_SIDEPANEL_MODE_FAVORITES_DETAILS,
    ].includes(mode),
    isDebugSelected: mode === modes.DESIGN_SIDEPANEL_MODE_DEBUG,
    isRateSelected: mode === modes.DESIGN_SIDEPANEL_MODE_RATE,
}))
